import AutocompleteSelect from '@base/components/src/AutocompleteSelect';
import currencyFormatting from '@package/utils/src/currencyFormatting';
import { useCallback, useEffect, useState } from 'react';
import config from 'config-base';
import ProductCard from '../../ProductCard';

import { CartProductRowProps } from './types';
import { createQuantityOptions } from './utils';
import styles from './CartProductRow.module.css';
import clsx from 'clsx';

const CartProductRow = (props: Readonly<CartProductRowProps>) => {
  const {
    orderId,
    product,
    vendorName,
    deleteProduct,
    updateProductQuantity,
    onApplyCouponToItem,
    onGetTermsContent,
    readOnly = false,
    dataTestId,
    regularCouponApplied,
  } = props;
  const [productQuantity, setProductQuantity] = useState(product.quantity);
  const handleUpdateProductQuantity = useCallback(
    async (value: number) => {
      await updateProductQuantity({
        orderId,
        productItemId: product.seq,
        newQuantity: value,
      });
    },
    [product],
  );

  const isQuantityEditable = readOnly ? false : product.price && product.editable;
  const isProductRemovable = readOnly ? false : product.removeAvailable;
  useEffect(() => {
    if (productQuantity !== product.quantity) {
      setProductQuantity(product.quantity);
    }
  }, [product.quantity]);

  return (
    <article data-testid={dataTestId} className={styles.productRow}>
      <ProductCard
        vendorName={vendorName}
        product={product}
        imgLayout="responsive"
        readonly={readOnly}
        isProductRemovable={isProductRemovable}
        regularCouponApplied={regularCouponApplied}
        onRemoveProduct={async () =>
          await deleteProduct({
            productItemId: product.seq,
            orderId,
            product,
          })
        }
        onApplyCouponToItem={onApplyCouponToItem}
        onGetTermsContent={onGetTermsContent}
      />
      <div className={`${styles.productContainer} productContainer`}>
        <div className={styles.producActionContainer}>
          {isQuantityEditable ? (
            <span className={styles.quantityContainer}>
              <AutocompleteSelect
                data-testid="product-qty"
                options={createQuantityOptions(product.purchaseLimit ?? 0)}
                value={{
                  label: productQuantity.toString().trim(),
                  value: productQuantity,
                }}
                onChange={(option: any) => {
                  void handleUpdateProductQuantity(option.value);
                }}
                name="qty"
                size="small"
                variant="square"
                className={clsx(styles.quantitySelect, { [styles.quantitySelectRounded]: config.brandCode === '0' })}
                masked
                mask="qty"
                disableAutoComplete={true}
              />
            </span>
          ) : (
            <div data-testid="product-qty-no-editable" className={styles.quantityContainer}>
              <span
                className={clsx(styles.freeProductQuantity, {
                  [styles.freeProductQuantityRounded]: config.brandCode === '0',
                })}
              >
                {product.quantity}
              </span>
            </div>
          )}
          {isProductRemovable && (
            <button
              className={`${styles.removeButton} removeButton`}
              onClick={async () =>{
                await deleteProduct({
                  productItemId: product.seq,
                  orderId,
                  product,
                })
              }}
              data-testid="remove-product"
            >
              <img src="/marketplace-static/images/icons/trashIcon.svg" alt="trash icon" width={20} height={20} />
              <span>Remove</span>
            </button>
          )}
        </div>

        <span className={clsx(styles.price, { [styles.freePrice]: product.price === 0 })} data-testid="product-price">
          {currencyFormatting(product.price)}
        </span>
      </div>
    </article>
  );
};

export default CartProductRow;
